import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Trophy, School, ArrowRight, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateSearchResultsPdf } from "@/lib/pdf";
import { Highlight } from "@/lib/highlight";
import { toast } from "sonner";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [records, setRecords] = useState([]);
  const [sports, setSports] = useState([]);
  const [settings, setSettings] = useState({});
  const [recordTypesBySport, setRecordTypesBySport] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const abortRef = useRef();
  const debounceRef = useRef();

  useEffect(() => {
    api.get("/sports").then(r => setSports(r.data)).catch(() => {});
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  // Load record types for every sport so Master Search can deep-link to the
  // correct event sub-tab when a record type is grouped by event.
  useEffect(() => {
    if (sports.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(sports.map(async s => {
        try { const { data } = await api.get(`/record-types?sport_id=${s.id}`); return [s.id, data]; }
        catch { return [s.id, []]; }
      }));
      if (!cancelled) setRecordTypesBySport(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [sports]);

  // Debounced live search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setRecords([]); setHasSearched(false); setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const { data } = await api.get(`/records`, {
          params: { search: trimmed, status: "approved", page: 0, page_size: 500 },
          signal: controller.signal,
        });
        // New paginated shape returns { items, total }; fall back to array for legacy.
        setRecords(Array.isArray(data) ? data : (data.items || []));
        setHasSearched(true);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setRecords([]);
        }
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const sportById = useMemo(() => {
    const map = {};
    sports.forEach(s => { map[s.id] = s; });
    return map;
  }, [sports]);

  // Group: sport → record_type → records
  const grouped = useMemo(() => {
    const g = {};
    for (const r of records) {
      const sportKey = r.sport_id;
      const typeKey = r.record_type || "General";
      if (!g[sportKey]) g[sportKey] = { sport: sportById[sportKey], types: {} };
      if (!g[sportKey].types[typeKey]) g[sportKey].types[typeKey] = [];
      g[sportKey].types[typeKey].push(r);
    }
    // Sort sports by name; sort records within each type by year desc
    const arr = Object.values(g).sort((a, b) => (a.sport?.name || "").localeCompare(b.sport?.name || ""));
    arr.forEach(entry => {
      Object.values(entry.types).forEach(list => list.sort((a, b) => (b.year || "").localeCompare(a.year || "")));
    });
    return arr;
  }, [records, sportById]);

  const totalCount = records.length;

  // (local Highlight helper removed — now uses shared @/lib/highlight)

  return (
    <main className="max-w-6xl mx-auto px-6 py-10" data-testid="search-page">
      <div className="mb-6">
        <p className="font-overline text-xs mb-2" style={{ color: "var(--brand-secondary)" }}>Master Search</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight" style={{ color: "var(--brand-primary)" }}>
          Every record. Every sport.
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Type an athlete name, school, or year to instantly find every matching record — across every sport and every record type.
        </p>
      </div>

      <div className="relative mb-4" data-testid="search-input-wrapper">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none"/>
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search across all record book fields..."
          className="pl-12 pr-14 h-14 text-lg rounded-sm border-2 focus-visible:ring-0"
          style={{ borderColor: "var(--brand-primary)" }}
          autoFocus
          data-testid="master-search-input"
        />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" data-testid="search-spinner"/>}
        {!loading && q.trim().length >= 2 && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-overline text-slate-500" data-testid="result-count">
            {totalCount} {totalCount === 1 ? "record" : "records"}
          </span>
        )}
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-end mb-6" data-testid="pdf-toolbar">
          <Button
            onClick={async () => {
              setDownloading(true);
              try {
                await generateSearchResultsPdf({ query: q.trim(), settings, groups: grouped, totalCount });
                toast.success("PDF downloaded");
              } catch (e) {
                toast.error("Failed to generate PDF");
              } finally { setDownloading(false); }
            }}
            disabled={downloading}
            className="rounded-sm h-11 px-5 font-heading tracking-wide"
            style={{ background: "var(--brand-primary)", color: "white" }}
            data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
            {downloading ? "Preparing PDF..." : "Download as PDF"}
          </Button>
        </div>
      )}

      {q.trim().length > 0 && q.trim().length < 2 && (
        <p className="text-sm text-slate-500 italic" data-testid="min-chars-hint">Keep typing... at least 2 characters</p>
      )}

      {hasSearched && !loading && totalCount === 0 && q.trim().length >= 2 && (
        <div className="border-2 border-dashed border-slate-300 p-16 text-center" data-testid="no-results">
          <School className="w-10 h-10 mx-auto text-slate-400 mb-3"/>
          <p className="font-heading text-xl text-slate-700 mb-1">No records found for this school</p>
          <p className="text-sm text-slate-500">
            We couldn't find any records matching "<strong>{q}</strong>". Try a shorter or different spelling.
          </p>
        </div>
      )}

      {grouped.length > 0 && (
        <div className="space-y-8" data-testid="results-groups">
          {grouped.map(({ sport, types }) => {
            const sportPrimary = sport?.primary_color || "var(--brand-primary)";
            const sportAccent = sport?.accent_color || "var(--brand-accent)";
            const sportTotal = Object.values(types).reduce((n, arr) => n + arr.length, 0);
            return (
              <section key={sport?.id || "unknown"} className="border-2 bg-white" style={{ borderColor: sportPrimary }} data-testid={`group-sport-${sport?.slug || "unknown"}`}>
                <header className="p-4 flex items-center justify-between text-white" style={{ background: sportPrimary }}>
                  <div className="flex items-center gap-3">
                    {sport?.logo_url ? (
                      <img src={sport.logo_url} alt="" className="h-9 w-9 bg-white/95 p-0.5 object-contain"/>
                    ) : (
                      <div className="h-9 w-9 flex items-center justify-center" style={{ background: sportAccent }}>
                        <Trophy className="w-4 h-4" style={{ color: sportPrimary }} strokeWidth={2.5}/>
                      </div>
                    )}
                    <div>
                      <p className="font-overline text-[10px] opacity-80">{sportTotal} {sportTotal === 1 ? "record" : "records"} · {Object.keys(types).length} record {Object.keys(types).length === 1 ? "type" : "types"}</p>
                      <h2 className="font-heading text-2xl tracking-tight">{sport?.name?.toUpperCase() || "UNKNOWN SPORT"}</h2>
                    </div>
                  </div>
                  {sport?.slug && (
                    <Link to={`/sports/${sport.slug}`} className="text-xs font-heading tracking-wide hover:underline flex items-center gap-1" data-testid={`view-sport-${sport.slug}`}>
                      Full record book <ArrowRight className="w-3.5 h-3.5"/>
                    </Link>
                  )}
                </header>
                <div className="p-4 space-y-5">
                {Object.entries(types).map(([typeName, list]) => {
                    // Dynamic column set for this record type: union of columns present on visible rows.
                    const cols = (() => {
                      const seen = new Set(); const out = [];
                      list.forEach(r => Object.keys(r.dynamic_columns || {}).forEach(c => {
                        if (c && !seen.has(c)) { seen.add(c); out.push(c); }
                      }));
                      return out;
                    })();
                    return (
                    <div key={typeName} data-testid={`group-type-${sport?.slug}-${typeName}`}>
                      <div className="flex items-baseline gap-3 mb-2 pb-1 border-b" style={{ borderColor: sportAccent }}>
                        <Badge className="rounded-sm border font-heading tracking-wide" style={{ background: sportAccent, color: sportPrimary, borderColor: sportAccent }}>
                          {typeName.toUpperCase()}
                        </Badge>
                        <span className="text-[10px] font-overline text-slate-500">{list.length} {list.length === 1 ? "record" : "records"}</span>
                      </div>
                      {cols.length === 0 ? (
                        <div className="border border-dashed border-slate-300 p-4 text-xs text-slate-500 italic">
                          No fields defined for this record type.
                        </div>
                      ) : (
                        <div className="border border-slate-200 overflow-x-auto">
                          <Table>
                            <TableHeader className="bg-slate-50">
                              <TableRow>
                                <TableHead className="text-xs font-heading whitespace-nowrap">Category</TableHead>
                                {cols.map(c => (
                                  <TableHead key={c} className="text-xs font-heading whitespace-nowrap">{c}</TableHead>
                                ))}
                                <TableHead className="text-xs font-heading whitespace-nowrap w-10"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {list.map(r => {
                                // Deep-link to the sport page's active record-type tab AND the
                                // event sub-tab (if the record type is grouped by an event column).
                                const rtDef = (recordTypesBySport[sport?.id] || []).find(t => t.name === typeName);
                                const eventCol = rtDef?.group_by_event_tabs ? rtDef.event_column : null;
                                const eventVal = eventCol ? (r.dynamic_columns?.[eventCol] || "").toString().trim() : "";
                                const params = new URLSearchParams();
                                if (typeName) params.set("rt", typeName);
                                if (eventVal) params.set("event", eventVal);
                                if (q) params.set("q", q);
                                const href = sport?.slug ? `/sports/${sport.slug}?${params.toString()}` : "#";
                                return (
                                <TableRow key={r.id} data-testid={`result-row-${r.id}`} className="hover:bg-slate-50">
                                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{r.category}</TableCell>
                                  {cols.map(c => (
                                    <TableCell key={c} className="text-sm whitespace-nowrap">
                                      <Highlight text={r.dynamic_columns?.[c] ?? ""} q={q}/>
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right">
                                    <Link
                                      to={href}
                                      className="inline-flex items-center gap-1 text-xs font-heading tracking-wide hover:underline"
                                      style={{ color: sportPrimary }}
                                      data-testid={`goto-record-${r.id}`}
                                      title="Open in the record book"
                                    >
                                      <ArrowRight className="w-3.5 h-3.5"/>
                                    </Link>
                                  </TableCell>
                                </TableRow>
                              );})}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!hasSearched && !loading && q.trim().length < 2 && (
        <div className="mt-6 grid md:grid-cols-3 gap-3 text-sm text-slate-600">
          <div className="border-l-4 pl-3" style={{ borderColor: "var(--brand-primary)" }}>
            <p className="font-overline text-[10px] text-slate-500">Search by</p>
            <p>Any imported field — record type, category, or any CSV column value</p>
          </div>
          <div className="border-l-4 pl-3" style={{ borderColor: "var(--brand-secondary)" }}>
            <p className="font-overline text-[10px] text-slate-500">Results are</p>
            <p>Grouped by sport, then by record type — with player, year and stat value</p>
          </div>
          <div className="border-l-4 pl-3" style={{ borderColor: "var(--brand-accent)" }}>
            <p className="font-overline text-[10px] text-slate-500">Live</p>
            <p>Results update instantly as you type — no need to press enter</p>
          </div>
        </div>
      )}
    </main>
  );
}
